/**
 * Answers-key convention helpers — shared by engine/relevance.js and engine/matrix.js.
 *
 * Convention (see .claude/skills/farmer-survey-domain/references/answers-key-convention.md
 * and §4 of the same skill): a flat `answers` object keyed by
 *   "<question_name>"                          — top-level question
 *   "<repeatName>#<idx>.<question_name>"        — question inside a repeat instance
 *   "<repeatA>#<i>.<repeatB>#<j>.<question_name>" — nested repeats, prefixes concatenated
 *
 * `prefix` is always either '' or a string ending in '.' (e.g. "fertilizer_time#0.").
 *
 * TODO(@farmer/audit-rules): once packages/audit-rules/src/keys.ts lands with equivalent
 * parseKey/buildKey/repeatRows exports, delete this file and import from there instead —
 * this is intentionally a byte-for-byte port of the convention so the switch is a no-op.
 */

/** Strip the last "<name>#<idx>." segment off a prefix, returning the parent prefix. */
export function parentPrefix(prefix) {
  return String(prefix || '').replace(/[^.]*#\d+\.$/, '');
}

/** Build the prefix for a given repeat name + index, appended to a parent prefix. */
export function buildPrefix(parentPfx, repeatName, idx) {
  return `${parentPfx || ''}${repeatName}#${idx}.`;
}

/** Build a full answers key for a field name under a given prefix. */
export function buildKey(prefix, name) {
  return `${prefix || ''}${name}`;
}

/**
 * How many instances of `repeatName` currently exist under `parentPrefix`, based on the
 * highest index seen among keys of the form `${parentPrefix}${repeatName}#<idx>.*`.
 * Counts by presence of *any* key at that index (including keys of nested repeats).
 */
export function repeatCount(answers, repeatName, parentPfx = '') {
  const re = new RegExp(
    `^${escapeRegExp(parentPfx)}${escapeRegExp(repeatName)}#(\\d+)\\.`,
  );
  let max = -1;
  for (const k of Object.keys(answers)) {
    const m = k.match(re);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return max + 1;
}

function escapeRegExp(s) {
  return String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Parse a flat answers key into { segments: [{repeatName, idx}], name }.
 * e.g. "fertilizer_time#0.fertilizer_name" ->
 *   { segments: [{repeatName:'fertilizer_time', idx:0}], name: 'fertilizer_name' }
 */
export function parseKey(key) {
  const parts = String(key || '').split('.');
  const name = parts.pop();
  const segments = parts.map((p) => {
    const m = p.match(/^(.*)#(\d+)$/);
    return m ? { repeatName: m[1], idx: parseInt(m[2], 10) } : { repeatName: p, idx: 0 };
  });
  return { segments, name };
}
